import { useState, useRef, useEffect } from "react";
import { Workflow } from "../../types";

interface WorkflowDropdownProps {
    label: string;
    availableWorkflows: Workflow[];
    selectedWorkflow: Workflow | null;
    onSelectionChange: (selected: Workflow | null) => void;
    isLoading: boolean;
}

const WorkflowDropdown: React.FC<WorkflowDropdownProps> = ({ 
    label, 
    availableWorkflows, 
    selectedWorkflow, 
    onSelectionChange, 
    isLoading 
}) => {
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredWorkflows = availableWorkflows.filter((workflow) =>
        workflow.wf_name?.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelection = (workflow: Workflow) => {
        onSelectionChange(workflow);
        setIsOpen(false);
    };

    return (
        <div className="relative flex-1" ref={dropdownRef}>
            <label className="block text-gray-700 font-semibold mb-2">{label}</label>

            {/* Selected Value */}
            <div
                className="relative min-w-[200px] border rounded-lg p-2 cursor-pointer bg-white flex items-center justify-between"
                onClick={() => !isLoading && setIsOpen(!isOpen)}
            >
                <span className="truncate">
                    {selectedWorkflow ? selectedWorkflow.wf_name : "Select a workflow"}
                </span>
                <span className="ml-2">&#9662;</span> {/* Dropdown Arrow */}
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div className="absolute z-10 flex flex-col mt-2 bg-white border rounded-lg shadow-lg min-w-[200px] max-w-[300px]">
                    {isLoading ? (
                        <div className="p-4 text-center">
                            <span className="animate-spin inline-block w-6 h-6 border-4 border-gray-300 border-t-blue-500 rounded-full"></span>
                        </div>
                    ) : (
                        <>
                            <input
                                type="text"
                                className="w-full p-2 border-b focus:outline-none"
                                placeholder="Search workflows..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                            <div className="max-h-40 overflow-y-auto">
                                {filteredWorkflows.length > 0 ? (
                                    filteredWorkflows.map((workflow) => (
                                        <div
                                            key={workflow.wf_definition_id}
                                            className="p-2 cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSelection(workflow)}
                                        >
                                            {workflow.wf_name}
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-2 text-gray-500 text-center">No workflows found</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default WorkflowDropdown;
